import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { normalizeOrg } from '../../lib/utils';
import { ShieldAlert, User, Building2, AlertCircle, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';

export const DataAuditTool: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'people' | 'orgs'>('people');
  const { data: auditData, isLoading, refetch } = useQuery({
    queryKey: ['system-data-audit'],
    queryFn: async () => {
      const [peopleRes, orgsRes] = await Promise.all([
        supabase.from('people').select('id, first_name, last_name, email, organization_id, organizations(name)'),
        supabase.from('organizations').select('id, name')
      ]);

      const people = peopleRes.data || [];
      const orgs = orgsRes.data || [];

      // Identity Collisions (Same Name, Different Email)
      const nameMap: Record<string, any[]> = {};
      people.forEach(p => {
        const key = `${p.first_name} ${p.last_name}`.toLowerCase().trim();
        if (!nameMap[key]) nameMap[key] = [];
        nameMap[key].push(p);
      });
      const identityCollisions = Object.entries(nameMap)
        .filter(([_, group]) => group.length > 1)
        .map(([name, group]) => ({ name, records: group }));

      // Normalization Collisions (Different Raw, Same Normalized)
      const normMap: Record<string, any[]> = {};
      orgs.forEach(o => {
        const key = normalizeOrg(o.name);
        if (!normMap[key]) normMap[key] = [];
        normMap[key].push(o);
      });
      const orgCollisions = Object.entries(normMap)
        .filter(([_, group]) => group.length > 1)
        .map(([key, group]) => ({ key, records: group }));

      return { identityCollisions, orgCollisions };
    }
  });

  if (isLoading) {
    return (
      <div className="p-20 flex flex-col items-center justify-center text-center">
        <Loader2 className="animate-spin text-slate-900 mb-6" size={48} />
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300">Scanning Registry for Anomaly Patterns</p>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="bg-slate-900 p-10 rounded-[2rem] text-white flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
        <div className="flex items-center gap-8 relative z-10">
          <div className="w-20 h-20 bg-rose-500 rounded-3xl flex items-center justify-center text-white shadow-2xl">
            <ShieldAlert size={40} />
          </div>
          <div>
            <h3 className="text-2xl font-black uppercase tracking-tight leading-none mb-2">Registry Quality Audit</h3>
            <p className="text-xs text-slate-400 uppercase font-bold tracking-widest italic">Heuristic Anomaly Detection Engine</p>
          </div>
        </div>
        <Button 
          onClick={() => refetch()} 
          variant="outline" 
          className="bg-white/5 border-white/10 text-white hover:bg-white/10"
          icon={<RefreshCw size={18} />}
        >
          Re-Scan Database
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-[2rem] overflow-hidden shadow-sm min-h-[500px] flex flex-col">
        <div className="bg-slate-50 border-b border-slate-100 p-2 flex gap-2">
           <button 
            onClick={() => setActiveTab('people')}
            className={`flex-1 py-4 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'people' ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
           >
             <User size={14} className="inline mr-2" /> People Audit ({auditData?.identityCollisions.length})
           </button>
           <button 
            onClick={() => setActiveTab('orgs')}
            className={`flex-1 py-4 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'orgs' ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
           >
             <Building2 size={14} className="inline mr-2" /> Organization Audit ({auditData?.orgCollisions.length})
           </button>
        </div>

        <div className="p-10 flex-1">
          {activeTab === 'people' ? (
            <div className="space-y-6">
              {auditData?.identityCollisions.length === 0 ? (
                <div className="p-20 text-center flex flex-col items-center gap-4">
                  <CheckCircle2 size={48} className="text-emerald-500" />
                  <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest italic">Zero Identity Collisions Detected</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {auditData?.identityCollisions.map((collision, idx) => (
                    <div key={idx} className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="px-6 py-3 bg-white border-b border-slate-100 flex items-center justify-between">
                         <span className="text-[11px] font-black text-slate-900 uppercase">{collision.name}</span>
                         <span className="text-[8px] font-black bg-rose-50 text-rose-500 px-2 py-0.5 rounded border border-rose-100 uppercase tracking-tighter">{collision.records.length} DUPLICATES</span>
                      </div>
                      <div className="divide-y divide-slate-100">
                         {collision.records.map(rec => (
                           <div key={rec.id} className="p-4 flex items-center justify-between text-[10px]">
                              <div className="space-y-0.5">
                                 <p className="text-slate-900 font-bold lowercase">{rec.email || '—'}</p>
                                 <p className="text-slate-400 font-bold uppercase tracking-tight">{rec.organizations?.name || 'Independent'}</p>
                              </div>
                           </div>
                         ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {auditData?.orgCollisions.length === 0 ? (
                <div className="p-20 text-center flex flex-col items-center gap-4">
                  <CheckCircle2 size={48} className="text-emerald-500" />
                  <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest italic">Zero Institutional Collisions Detected</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {auditData?.orgCollisions.map((collision, idx) => (
                    <div key={idx} className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="px-6 py-3 bg-white border-b border-slate-100 flex items-center justify-between">
                         <span className="text-[11px] font-black text-slate-900 uppercase">{collision.key}</span>
                         <span className="text-[8px] font-black bg-purple-50 text-purple-600 px-2 py-0.5 rounded border border-purple-100 uppercase tracking-tighter">{collision.records.length} ALIASES</span>
                      </div>
                      <div className="divide-y divide-slate-100">
                         {collision.records.map(rec => (
                           <div key={rec.id} className="p-4 flex items-center justify-between text-[10px]">
                              <p className="text-slate-900 font-bold uppercase tracking-tight">{rec.name}</p>
                           </div>
                         ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 p-8 rounded-[2rem] flex items-center gap-8">
         <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-blue-600 shadow-sm border border-blue-100 shrink-0">
            <AlertCircle size={24} />
         </div>
         <div>
            <h4 className="text-[10px] font-black text-blue-900 uppercase tracking-widest mb-1 leading-none">Administrative Protocol</h4>
            <p className="text-blue-700 text-[9px] font-bold uppercase leading-relaxed tracking-wide italic">The audit tool detects potential data fragmentation. Collisions are flagged based on exact name matches or institutional normalization overlap.</p>
         </div>
      </div>
    </div>
  );
};
